import { BlogEditorRightToolBarActionMarkEnum } from '@doltech/utils/lib/constants';
import { values } from 'lodash';
import { getRoot, types as t } from 'mobx-state-tree';

import { ELEMENT_H2, ELEMENT_H3, ELEMENT_H4, ELEMENT_PARAGRAPH } from '../../slatePlugins';
import { MARK_BOLD } from '../../slatePlugins/marks/bold/defaults';
import { MARK_ITALIC } from '../../slatePlugins/marks/italic/defaults';
import { MARK_STRIKETHROUGH } from '../../slatePlugins/marks/strikethrough/defaults';
import { BlogEditorSelectStateStoreInstance } from './blog-editor-select-state.store';
import { ELEMENT_BULLET_POINT } from './plugins/BulletPoint/BulletPointPlugin.types';
import {
  TEXT_TRANSFORM_LOWER_CASE,
  TEXT_TRANSFORM_TITLE_CASE,
  TEXT_TRANSFORM_UPPER_CASE,
} from './plugins/TextTransform/types';
import { K12_ELEMENT_QUOTE } from './plugins/K12Quote/K12QuotePlugin.types';
import {
  ELEMENT_ALIGN_CENTER,
  ELEMENT_ALIGN_JUSTIFY,
  ELEMENT_ALIGN_LEFT,
  ELEMENT_ALIGN_RIGHT,
} from '../../slatePlugins/elements/align/defaults';
import { MARK_Q } from '../../slatePlugins/marks/q/defaults';
import { MARK_ABBR } from '../../slatePlugins/marks/abbr/defaults';
import { MARK_CITE } from '../../slatePlugins/marks/cite/defaults';
import { MARK_UNDERLINE } from '../../slatePlugins/marks/underline/defaults';

export const BlogEditorRightSidebarStore = t
  .model('BlogEditorRightSidebarStore', {
    menuEnabled: t.optional(t.array(t.string), values(BlogEditorRightToolBarActionMarkEnum) as any),
    menuActive: t.optional(t.array(t.string), []),
  })
  .views((self) => ({
    getCardLogicRightSideBar() {
      const rootStore: any = getRoot(self);
      if (rootStore.selectState) {
        const { isInCardLogicCell, isInCardLogicHeader } =
          rootStore.selectState as BlogEditorSelectStateStoreInstance;

        if (isInCardLogicHeader) {
          return [
            BlogEditorRightToolBarActionMarkEnum.H3,
            BlogEditorRightToolBarActionMarkEnum.EMOJI,
          ];
        }

        if (isInCardLogicCell) {
          return values(BlogEditorRightToolBarActionMarkEnum).filter((markAction: any) => {
            return ![
              BlogEditorRightToolBarActionMarkEnum.H2,
              BlogEditorRightToolBarActionMarkEnum.H3,
              BlogEditorRightToolBarActionMarkEnum.QUOTE,
            ].includes(markAction);
          });
        }
      }
      return null;
    },
    getTableRightSideBar() {
      const rootStore: any = getRoot(self);
      if (rootStore.selectState) {
        const { isInTableCell, isInTableHeader } =
          rootStore.selectState as BlogEditorSelectStateStoreInstance;

        if (isInTableHeader) {
          return [
            BlogEditorRightToolBarActionMarkEnum.H3,
            BlogEditorRightToolBarActionMarkEnum.H4,
            BlogEditorRightToolBarActionMarkEnum.EMOJI,
          ];
        }

        if (isInTableCell) {
          return values(BlogEditorRightToolBarActionMarkEnum).filter((markAction: any) => {
            return ![
              BlogEditorRightToolBarActionMarkEnum.H4,
              BlogEditorRightToolBarActionMarkEnum.H3,
              BlogEditorRightToolBarActionMarkEnum.H2,
              BlogEditorRightToolBarActionMarkEnum.QUOTE,
            ].includes(markAction);
          });
        }
      }
      return null;
    },
  }))
  .views((self) => ({
    get menuEnabledBasedOnSelection() {
      const rootStore: any = getRoot(self);
      const selectState: BlogEditorSelectStateStoreInstance = rootStore?.selectState;
      if (selectState) {
        if (
          selectState.isDisableAll ||
          selectState.isInSpecialObject ||
          selectState.isInSampleSpecialObject ||
          selectState.isInBlankV2
        ) {
          return [];
        }
        if (selectState.isExtraLearningPointEditor) {
          return values(BlogEditorRightToolBarActionMarkEnum).filter((markAction: any) => {
            return ![BlogEditorRightToolBarActionMarkEnum.H2].includes(markAction);
          });
        }
        if (selectState.isInSlideTextInput) {
          return values(BlogEditorRightToolBarActionMarkEnum).filter((markAction: any) => {
            return ![
              BlogEditorRightToolBarActionMarkEnum.H4,
              BlogEditorRightToolBarActionMarkEnum.H3,
              BlogEditorRightToolBarActionMarkEnum.H2,
              BlogEditorRightToolBarActionMarkEnum.ROUND_TEXT,
              BlogEditorRightToolBarActionMarkEnum.IMAGE,
              BlogEditorRightToolBarActionMarkEnum.QUOTE,
              BlogEditorRightToolBarActionMarkEnum.NUMBER_POINT,
              BlogEditorRightToolBarActionMarkEnum.RECORD,
            ].includes(markAction);
          });
        }
        if (
          selectState.isInExerciseMultipleChoice ||
          selectState.isInExerciseGapFill ||
          selectState.isInExerciseShortAnswer ||
          selectState.isInPluginExplanation
        ) {
          return values(BlogEditorRightToolBarActionMarkEnum).filter((markAction: any) => {
            return ![BlogEditorRightToolBarActionMarkEnum.H2].includes(markAction);
          });
        }
        if (!selectState.isTextSelection) {
          return [
            BlogEditorRightToolBarActionMarkEnum.H4,
            BlogEditorRightToolBarActionMarkEnum.H2,
            BlogEditorRightToolBarActionMarkEnum.H3,
            BlogEditorRightToolBarActionMarkEnum.PARAGRAPH,
            BlogEditorRightToolBarActionMarkEnum.ROUND_TEXT,
            BlogEditorRightToolBarActionMarkEnum.IMAGE,
            BlogEditorRightToolBarActionMarkEnum.NUMBER_POINT,
            BlogEditorRightToolBarActionMarkEnum.QUOTE,
            BlogEditorRightToolBarActionMarkEnum.BULLET_POINT,
            BlogEditorRightToolBarActionMarkEnum.EMOJI,
            BlogEditorRightToolBarActionMarkEnum.ALIGN_LEFT,
            BlogEditorRightToolBarActionMarkEnum.ALIGN_RIGHT,
            BlogEditorRightToolBarActionMarkEnum.ALIGN_CENTER,
            BlogEditorRightToolBarActionMarkEnum.ALIGN_JUSTIFY,
            BlogEditorRightToolBarActionMarkEnum.INDENT_INCREASE,
            BlogEditorRightToolBarActionMarkEnum.INDENT_DECREASE,
            BlogEditorRightToolBarActionMarkEnum.MATH,
          ];
        }
        if (selectState.isInCardLogic && self.getCardLogicRightSideBar()) {
          return self.getCardLogicRightSideBar();
        }
        if (selectState.isInTable && self.getTableRightSideBar()) {
          return self.getTableRightSideBar();
        }
        if (selectState.isInQuoteBlock) {
          return [BlogEditorRightToolBarActionMarkEnum.QUOTE];
        }
        if (selectState.isInBulletPoint) {
          return values(BlogEditorRightToolBarActionMarkEnum).filter((markAction) => {
            return ![
              BlogEditorRightToolBarActionMarkEnum.H4,
              BlogEditorRightToolBarActionMarkEnum.H3,
              BlogEditorRightToolBarActionMarkEnum.H2,
              BlogEditorRightToolBarActionMarkEnum.QUOTE,
              BlogEditorRightToolBarActionMarkEnum.PARAGRAPH,
            ].includes(markAction as any);
          });
        }
        if (selectState.isHeading2 || selectState.isHeading3 || selectState.isHeading4) {
          return [
            BlogEditorRightToolBarActionMarkEnum.H4,
            BlogEditorRightToolBarActionMarkEnum.H2,
            BlogEditorRightToolBarActionMarkEnum.H3,
            BlogEditorRightToolBarActionMarkEnum.QUOTE,
            BlogEditorRightToolBarActionMarkEnum.EMOJI,
          ];
        }
        if (selectState.isInBorderShading) {
          return values(BlogEditorRightToolBarActionMarkEnum).filter((markAction: any) => {
            return ![
              BlogEditorRightToolBarActionMarkEnum.H4,
              BlogEditorRightToolBarActionMarkEnum.H3,
              BlogEditorRightToolBarActionMarkEnum.H2,
            ].includes(markAction);
          });
        }
      }
      return values(BlogEditorRightToolBarActionMarkEnum);
    },
    get menuActivedBasedOnSelection() {
      const rootStore: any = getRoot(self);
      const selectState: BlogEditorSelectStateStoreInstance = rootStore?.selectState;
      if (selectState) {
        return [
          // for element
          selectState.isHeading2 && BlogEditorRightToolBarActionMarkEnum.H2,
          selectState.isHeading3 && BlogEditorRightToolBarActionMarkEnum.H3,
          selectState.isHeading4 && BlogEditorRightToolBarActionMarkEnum.H4,
          selectState.isParagraph &&
            !selectState.isHeading2 &&
            !selectState.isHeading3 &&
            !selectState.isHeading4 &&
            BlogEditorRightToolBarActionMarkEnum.PARAGRAPH,
          selectState.isRoundedText && BlogEditorRightToolBarActionMarkEnum.ROUND_TEXT,
          selectState.isNumberingElement && BlogEditorRightToolBarActionMarkEnum.NUMBER_POINT,
          selectState.isInQuoteBlock && BlogEditorRightToolBarActionMarkEnum.QUOTE,
          selectState.isInBulletPoint && BlogEditorRightToolBarActionMarkEnum.BULLET_POINT,

          // for mark
          selectState.isBold && BlogEditorRightToolBarActionMarkEnum.BOLD,
          selectState.isItalic && BlogEditorRightToolBarActionMarkEnum.ITALIC,
          selectState.isStrikeThrough && BlogEditorRightToolBarActionMarkEnum.STRIKE_THROUGH,
          selectState.isTitleCase && BlogEditorRightToolBarActionMarkEnum.TITLE_CASE,
          selectState.isLowerCase && BlogEditorRightToolBarActionMarkEnum.LOWER_CASE,
          selectState.isUpperCase && BlogEditorRightToolBarActionMarkEnum.UPPER_CASE,
          selectState.isCite && BlogEditorRightToolBarActionMarkEnum.CITE,
          selectState.isAbbr && BlogEditorRightToolBarActionMarkEnum.ABBR,
          selectState.isQ && BlogEditorRightToolBarActionMarkEnum.Q,
          selectState.isUnderline && BlogEditorRightToolBarActionMarkEnum.OLD_UNDERLINE,

          selectState.underlineColor?.length > 0 && BlogEditorRightToolBarActionMarkEnum.UNDERLINE,
          selectState.currentTextColor?.length > 0 &&
            BlogEditorRightToolBarActionMarkEnum.TEXT_COLOR,
          selectState.highlightElementColor?.length > 0 &&
            BlogEditorRightToolBarActionMarkEnum.HIGHLIGHT_TEXT_COLOR,
        ].filter(Boolean);
      }
      return [];
    },
  }))
  .views((self) => ({
    getFormatActionDisabledByType(type) {
      return !self.menuEnabledBasedOnSelection.includes(type);
    },
    getFormatActionActiveByType(type) {
      return self.menuActivedBasedOnSelection.includes(type);
    },
  }))
  .actions((self) => ({
    async formatElementByType({
      type,
      editor,
    }: {
      type: BlogEditorRightToolBarActionMarkEnum;
      editor: any;
    }) {
      const rootStore: any = getRoot(self);
      const selectState: BlogEditorSelectStateStoreInstance = rootStore?.selectState;

      switch (type) {
        case BlogEditorRightToolBarActionMarkEnum.H2: {
          const { toggleNodeType } = await import('../../slatePlugins/transforms/toggleNodeType');

          toggleNodeType(editor, {
            activeType: ELEMENT_H2,
          });
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.H3: {
          if (selectState && selectState.isInCardLogicHeader) {
            const { CardLogicTransformActions } = await import(
              './plugins/CardLogic/model/transforms'
            );

            CardLogicTransformActions.toggleH3EnabledAtSelection({ editor });
            break;
          }
          const { toggleNodeType } = await import('../../slatePlugins/transforms/toggleNodeType');

          toggleNodeType(editor, {
            activeType: ELEMENT_H3,
          });
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.H4: {
          const { toggleNodeType } = await import('../../slatePlugins/transforms/toggleNodeType');

          toggleNodeType(editor, {
            activeType: ELEMENT_H4,
          });
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.PARAGRAPH: {
          const { toggleNodeType } = await import('../../slatePlugins/transforms/toggleNodeType');

          toggleNodeType(editor, {
            activeType: ELEMENT_PARAGRAPH,
          });
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.BULLET_POINT: {
          const { BulletPointTransformActions } = await import(
            './plugins/BulletPoint/model/transforms'
          );

          BulletPointTransformActions.toggleListElement(editor, {
            type: ELEMENT_BULLET_POINT,
            forceInActive: true,
          });
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.QUOTE: {
          const { QuoteElementTransformActions } = await import('./plugins/Quote/model/transforms');
          QuoteElementTransformActions.addOrDeleteQuote(editor);
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.K12_ELEMENT_QUOTE: {
          const { toggleBlockAtSelection } = await import(
            '../DolEditor/helpers/toggleBlockAtSelection'
          );

          toggleBlockAtSelection(editor, K12_ELEMENT_QUOTE);
          break;
        }

        case BlogEditorRightToolBarActionMarkEnum.NUMBER_POINT: {
          const { questionStyleTransformActions } = await import(
            './plugins/QuestionStyle/model/transforms'
          );

          questionStyleTransformActions.toggleQuestionStyleAtSelection(editor);
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.ITALIC: {
          const { toggleMark } = await import('../../slatePlugins/transforms/toggleMark');

          toggleMark(editor, MARK_ITALIC);
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.BOLD: {
          const { toggleMark } = await import('../../slatePlugins/transforms/toggleMark');

          toggleMark(editor, MARK_BOLD);
          break;
        }

        case BlogEditorRightToolBarActionMarkEnum.STRIKE_THROUGH: {
          const { toggleMark } = await import('../../slatePlugins/transforms/toggleMark');

          toggleMark(editor, MARK_STRIKETHROUGH);
          break;
        }

        case BlogEditorRightToolBarActionMarkEnum.TITLE_CASE: {
          const { toggleTextTransformMark } = await import('../DolEditor/helpers');

          toggleTextTransformMark(editor, TEXT_TRANSFORM_TITLE_CASE);
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.UPPER_CASE: {
          const { toggleTextTransformMark } = await import('../DolEditor/helpers');

          toggleTextTransformMark(editor, TEXT_TRANSFORM_UPPER_CASE);
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.LOWER_CASE: {
          const { toggleTextTransformMark } = await import('../DolEditor/helpers');

          toggleTextTransformMark(editor, TEXT_TRANSFORM_LOWER_CASE);
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.ROUND_TEXT: {
          const { wrapRoundedAtSelection } = await import(
            '../DolEditor/elements/rounded/transform/wrapRoundedAtSelection'
          );

          wrapRoundedAtSelection(editor);
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.ALIGN_LEFT: {
          const { upsertAlign } = await import(
            '../../slatePlugins/elements/align/transforms/upsertAlign'
          );
          upsertAlign(editor, { type: ELEMENT_ALIGN_LEFT });
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.ALIGN_RIGHT: {
          const { upsertAlign } = await import(
            '../../slatePlugins/elements/align/transforms/upsertAlign'
          );
          upsertAlign(editor, { type: ELEMENT_ALIGN_RIGHT });
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.ALIGN_CENTER: {
          const { upsertAlign } = await import(
            '../../slatePlugins/elements/align/transforms/upsertAlign'
          );
          upsertAlign(editor, { type: ELEMENT_ALIGN_CENTER });
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.ALIGN_JUSTIFY: {
          const { upsertAlign } = await import(
            '../../slatePlugins/elements/align/transforms/upsertAlign'
          );
          upsertAlign(editor, { type: ELEMENT_ALIGN_JUSTIFY });
          break;
        }

        case BlogEditorRightToolBarActionMarkEnum.INDENT_INCREASE: {
          const { wrapIndent } = await import(
            '../../slatePlugins/elements/indent/transforms/toggleIndent'
          );
          wrapIndent(editor);
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.INDENT_DECREASE: {
          const { unwrapIndent } = await import(
            '../../slatePlugins/elements/indent/transforms/toggleIndent'
          );
          unwrapIndent(editor);
          break;
        }

        case BlogEditorRightToolBarActionMarkEnum.MATH: {
          const { addMathExpression } = await import('./plugins/Math/transforms/addMathExpression');
          addMathExpression(editor);
          break;
        }

        case BlogEditorRightToolBarActionMarkEnum.CITE: {
          const { toggleMark } = await import('../../slatePlugins/transforms/toggleMark');
          toggleMark(editor, MARK_CITE);
          break;
        }

        case BlogEditorRightToolBarActionMarkEnum.ABBR: {
          const { toggleMark } = await import('../../slatePlugins/transforms/toggleMark');
          toggleMark(editor, MARK_ABBR);
          break;
        }

        case BlogEditorRightToolBarActionMarkEnum.Q: {
          const { toggleMark } = await import('../../slatePlugins/transforms/toggleMark');

          toggleMark(editor, MARK_Q);
          break;
        }

        case BlogEditorRightToolBarActionMarkEnum.RECORD: {
          break;
        }

        // Action with custom menu -> DO NOTHING
        case BlogEditorRightToolBarActionMarkEnum.OLD_UNDERLINE: {
          const { toggleMark } = await import('../../slatePlugins/transforms/toggleMark');
          toggleMark(editor, MARK_UNDERLINE);
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.UNDERLINE:
        case BlogEditorRightToolBarActionMarkEnum.EMOJI:
        case BlogEditorRightToolBarActionMarkEnum.TEXT_COLOR:
        case BlogEditorRightToolBarActionMarkEnum.HIGHLIGHT_TEXT_COLOR: {
          // DO NOTHING
          break;
        }
        case BlogEditorRightToolBarActionMarkEnum.IMAGE: {
          const { addMedia } = await import('../DolEditor/elements/table-blog/transforms/addTable');
          addMedia(editor, type);
          break;
        }
        default: {
          // eslint-disable-next-line no-alert
          alert('Not implemented yet');
        }
      }
    },
  }));
